import { saveManifest } from '../storage/indexed-db.js';
import { setProgram, getProgram } from '../state/program-store.js';
import {
  evaluatePlaybackFromManifest,
  setInactiveReason,
  clearInactiveReason,
  shouldRotate,
} from '../state/playback-status-store.js';
import { refreshConnectionDisplay } from '../state/connection-status-store.js';
import { startRotation, showBlackScreen } from '../player/rotation.js';
import { normalizeProgram } from './program-version.js';
import { extractMediaUrls } from './diff.js';
import { prefetchMediaUrls } from '../storage/prefetch-media.js';
import { logInfo } from '../state/log-store.js';

/**
 * Persist manifest and apply playback rules.
 * @param {object} program
 * @param {HTMLElement | null} playerEl
 * @param {Date} [ref]
 * @param {{ prefetch?: boolean; fromCache?: boolean }} [opts]
 *   `fromCache: true` when applying a manifest loaded from IndexedDB (offline / refresh / boot guard).
 *   In that case the cached `active`/`inactiveReason` flag may be stale; local re-evaluation wins.
 * @returns {Promise<boolean>} true if rotation should run
 */
export async function applyProgramPlayback(program, playerEl, ref = new Date(), opts = {}) {
  program = normalizeProgram(program);
  await saveManifest(program);

  const trustApiActive = !opts.fromCache;
  const { shouldPlay, inactiveReason } = evaluatePlaybackFromManifest(program, ref, {
    trustApiActive,
  });

  if (!shouldPlay) {
    setInactiveReason(inactiveReason ?? program.inactiveReason ?? 'outside_schedule');
    setProgram(null);
    if (playerEl) showBlackScreen(playerEl);
    refreshConnectionDisplay();
    if (opts.prefetch !== false) {
      void prefetchMediaUrls(extractMediaUrls(program));
    }
    logInfo('Playback stopped', { reason: inactiveReason ?? program.inactiveReason });
    return false;
  }

  clearInactiveReason();
  const previous = getProgram();
  const wasPlaying = shouldRotate(previous);
  // Cached manifest with stale `active:false` must be normalized so that program-store's
  // own shouldRotate (default trustApiActive:true) keeps the program in state.
  const programToStore =
    opts.fromCache && program.active === false
      ? { ...program, active: true, inactiveReason: undefined }
      : program;
  setProgram(programToStore);
  refreshConnectionDisplay();
  if (playerEl && !wasPlaying) {
    startRotation(playerEl);
  }
  return true;
}
